
  import { defineComponent } from "vue";
  import { mapStores } from "pinia";

  import { useVuelidate } from "@vuelidate/core";
  import { get, isEmpty, set, trim, unset } from "lodash";
  import { v4 as uuidv4 } from "uuid";

  import { ProcessComponentsModes } from "@/enums/processComponentsModes.enum";
  import { useApplicationStore } from "@/stores/ApplicationStore";
  import { useApplicationComponentRegistry } from "@/stores/ApplicationComponentRegistry";
  import { resolveObjectEventNames, validationRules } from "@/helpers/utilsV2";
  import { ContextKeys } from "@/enums/contextKeys.enum";
  import { Events } from "@/enums/events.enum";
  import eventsProcessor from "@/mixins/eventsProcessor";
  import { ClientSideEvents } from "@/types";

  export default defineComponent({
    name: "BaseProcessComponentV2",

    mixins: [eventsProcessor],

    props: {
      settings: {
        type: Object,
        required: true,
      },

      components: {
        type: Array,
        default: () => [],
      },

      clientSideEvents: {
        type: Object,
        required: false,
        default: () => ({}),
      },

      id: {
        type: String,
        required: true,
      },

      indexPosition: {
        type: Number,
        default: () => -1,
      },

      type: {
        type: String,
        required: true,
      },

      viewMode: {
        type: String,
        default: () => ProcessComponentsModes.VISUALIZATION,
      },

      parent: {
        type: String,
        default: () => "",
      },

      path: {
        type: String,
        required: false,
        default: () => "",
      },

      contextPath: {
        type: String,
        required: false,
        default: () => "",
      },
    },

    data() {
      return {
        componentViewMode: this.viewMode || ("" as string),
        currentFile: null,
        defaultValue: null as any,
        eventHandlers: new Map<string, (payload: any) => void>(),
        internalValue: null as any,
        previousEvent: null as string | null,
        ref: uuidv4(),
        render: (this.settings?.attributes?.render ?? true) as boolean,
        rules: {} as any,
        serverErrors: [] as string[],
        viewModes: ProcessComponentsModes as any,
        v$: useVuelidate({ $externalResults: {} }) as any,
      };
    },

    async mounted() {
      if (!isEmpty(trim(this.attributes?.name))) {
        this.internalValue = get(this, this.path, this.defaultValue);
      }

      if (this.isReadOnly) {
        return;
      }

      this.rules = await validationRules(get(this.settings, "validation.rules", []));

      for (const eventName of Object.keys(this.resolvedClientSideEvents)) {
        if (eventName.startsWith("$form")) {
          const handler = (payload: any) => {
            this.processEvent({
              events: this.resolvedClientSideEvents,
              eventName: eventName,
              context: {
                [ContextKeys.THIS]: this,
                [ContextKeys.APPLICATION]: this.applicationStore.application,
                [ContextKeys.FORM]: get(this, this.root, {}),
                [ContextKeys.PAYLOAD]: payload,
              },
            });
          };

          this.eventHandlers.set(eventName, handler);
          this.$emitter.on(eventName, handler);
        }
      }

      // TODO: REMOVER
      await this.processEvent({
        events: this.resolvedClientSideEvents,
        eventName: `$this.${Events.LOAD}`,
        context: {
          [ContextKeys.THIS]: this,
          [ContextKeys.FORM]: get(this, this.root, {}),
        },
      });

      this.applicationComponentRegistryStore.register(this.ref, (eventName: Events) =>
        this.processEvent({
          events: this.resolvedClientSideEvents,
          eventName: `$this.${eventName}`,
          context: {
            [ContextKeys.THIS]: this,
            [ContextKeys.FORM]: get(this, this.root, {}),
            [ContextKeys.APPLICATION]: this.applicationStore.application,
          },
        })
      );

      await this.processEvent({
        events: this.resolvedClientSideEvents,
        eventName: `$this.${Events.MOUNTED}`,
        context: {
          [ContextKeys.THIS]: this,
          [ContextKeys.APPLICATION]: this.applicationStore.application,
          [ContextKeys.FORM]: get(this, this.root, {}),
        },
      });
    },

    async created() {
      // TODO: REMOVER
      await this.processEvent({
        events: this.resolvedClientSideEvents,
        eventName: `$this.${Events.INIT}`,
        context: {
          [ContextKeys.THIS]: this,
          [ContextKeys.FORM]: get(this, this.root, {}),
        },
      });

      await this.processEvent({
        events: this.resolvedClientSideEvents,
        eventName: `$this.${Events.CREATED}`,
        context: {
          [ContextKeys.THIS]: this,
          [ContextKeys.APPLICATION]: this.applicationStore.application,
          [ContextKeys.FORM]: get(this, this.root, {}),
        },
      });
    },

    unmounted() {
      if (this.isReadOnly) {
        return;
      }

      for (const [eventName, handler] of this.eventHandlers) {
        this.$emitter.off(eventName, handler);
      }

      this.eventHandlers.clear();

      this.applicationComponentRegistryStore.unregister(this.ref);

      if (this.attributes.name) {
        unset(this, this.path);
      }
    },

    computed: {
      ...mapStores(useApplicationStore, useApplicationComponentRegistry),

      attributes() {
        return this.settings.attributes;
      },

      context(): Record<string, any> {
        return this.contextPath.split(".").reduceRight((previousValue: any, currentValue: any): Record<string, any> => {
          return {
            id: currentValue,
            parent: previousValue,
          };
        }, {});
      },

      isReadOnly() {
        return this.componentViewMode === this.viewModes.VISUALIZATION;
      },

      resolvedClientSideEvents(): ClientSideEvents {
        return resolveObjectEventNames(this.clientSideEvents, this);
      },

      root(): string {
        return this.path.split(".")[0];
      },

      validationState() {
        return get(this.v$, this.path);
      },

      value: {
        get(): any {
          if (!isEmpty(trim(this.attributes?.name))) {
            return get(this, this.path, this.defaultValue);
          }

          return this.internalValue;
        },

        set(value: any) {
          if (!isEmpty(trim(this.attributes.name))) {
            let currentValue = get(this, this.path, this.defaultValue);

            if (currentValue !== value) {
              set(this, this.path, value);
            }
          }

          this.internalValue = value;
        },
      },
    },

    watch: {
      "applicationStore.serverValidationErrors": {
        handler(newValue) {
          if (newValue) {
            this.serverErrors = get(
              this.applicationStore.serverValidationErrors,
              `errors.${this.attributes.name}._schema`,
              [],
            );

            if (this.type === "form-repeater-v2") {
              const errors = get(
                this.applicationStore.serverValidationErrors,
                `errors.${this.attributes.name}`,
                []
              );

              const itemKey: any[] = Object.keys(get(this, "items", []));

              for (const [key, value] of Object.entries(errors)) {
                if (key !== "_schema") {
                  set(
                    this.applicationStore.serverValidationErrors,
                    `errors.${get(this, "formRepeaterContext", "")}.${itemKey[key as any]}`,
                    value,
                  );
                }
              }
            } else {
              const errorsPath: string = this.path.replace(/^form\.data\./, "");
              this.serverErrors = get(this.applicationStore.serverValidationErrors, `errors.${errorsPath}`, []);
            }
          }
        },
      },

      render(newVal: boolean) {
        if (!newVal && this.attributes.name) {
          unset(this, this.path);
        }
      },
    },

    validations() {
      let validations = {};
      const [root, ...rest] = this.path.split(".");

      set(validations, rest.join("."), { ...this.rules, $autoDirty: true });

      return {
        [root]: validations,
      };
    },
  });
